// The one-time sign-in tokens index.html moves out of the address bar before
// any analytics snippet can record them: the studio's ?signin= under
// sessionStorage 'studio_signin' (window.__studioSignin when storage is
// blocked), and the bot's ?token= (on a from=bot visit) under 'bot_signin'
// (window.__botSignin).

export type StashedSignin = "studio_signin" | "bot_signin";

const WINDOW_KEY: Record<StashedSignin, "__studioSignin" | "__botSignin"> = {
  studio_signin: "__studioSignin",
  bot_signin: "__botSignin",
};

/** The stashed token, removed as it is read so it is used once. */
export function takeStashedToken(key: StashedSignin): string | null {
  const w = window as Window & { __studioSignin?: string; __botSignin?: string };
  const windowKey = WINDOW_KEY[key];
  let token: string | null = null;
  try {
    token = window.sessionStorage.getItem(key);
    window.sessionStorage.removeItem(key);
  } catch {
    /* storage blocked: the inline script kept it on window instead */
  }
  if (!token && w[windowKey]) token = w[windowKey] as string;
  delete w[windowKey];
  return token;
}

// A SESSION THIS TAB GOT FROM TELEGRAM. A studio or bot sign-in (and a
// website sign-in Telegram confirmed) is remembered for the tab, by a
// fingerprint of the session, so a visit that comes back without a token
// (a cancelled checkout) can still be told apart from a session that was
// already in this browser for some other account. Only the tail of the
// session's signature is kept; storage that refuses means "not remembered".
const TELEGRAM_SESSION_KEY = "telegram_session";

const fingerprint = (session: string) => session.slice(-32);

/** Remember `session` as one Telegram signed in (null forgets). */
export function rememberTelegramSession(session: string | null): void {
  try {
    if (session) window.sessionStorage.setItem(TELEGRAM_SESSION_KEY, fingerprint(session));
    else window.sessionStorage.removeItem(TELEGRAM_SESSION_KEY);
  } catch {
    /* storage blocked: nothing is remembered */
  }
}

/** Whether `session` is the one a Telegram sign-in gave this tab. */
export function isTelegramSession(session: string | null): boolean {
  if (!session) return false;
  try {
    return window.sessionStorage.getItem(TELEGRAM_SESSION_KEY) === fingerprint(session);
  } catch {
    return false;
  }
}
